import React, { useState } from 'react';
import { useWebsiteData } from '../../../context/WebsiteDataContext';

const TestimonialsManager = () => {
  const { testimonials, addTestimonial, updateTestimonial, deleteTestimonial, toggleTestimonialStatus } = useWebsiteData();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');


  const filteredTestimonials = filterStatus === 'all'
    ? testimonials
    : testimonials.filter(t => t.status === filterStatus);

  const handleAddTestimonial = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const testimonialData = {
      clientName: formData.get('clientName'),
      company: formData.get('company'),
      service: formData.get('service'),
      rating: parseInt(formData.get('rating')),
      testimonial: formData.get('testimonial'),
      date: new Date().toISOString().split('T')[0]
    };

    addTestimonial(testimonialData);
    setShowAddModal(false);
  };

  const handleEditTestimonial = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const testimonialData = {
      clientName: formData.get('clientName'),
      company: formData.get('company'),
      service: formData.get('service'),
      rating: parseInt(formData.get('rating')),
      testimonial: formData.get('testimonial')
    };

    updateTestimonial(editingTestimonial.id, testimonialData);
    setEditingTestimonial(null);
  };

  const handleDeleteTestimonial = (testimonialId) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      deleteTestimonial(testimonialId);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < rating ? 'text-yellow-400' : 'text-gray-300'}>
        ★
      </span>
    ));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Testimonials Management</h2>
          <p className="text-gray-600">Manage client feedback and reviews</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-custom-brown text-white px-4 py-2 rounded-lg hover:bg-custom-brown/90 transition-colors duration-200"
        >
          Add Testimonial
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['all', 'published', 'pending'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${filterStatus === status
                ? 'border-custom-brown text-custom-brown'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              {status} ({testimonials.filter(t => status === 'all' ? true : t.status === status).length})
            </button>
          ))}
        </nav>
      </div>

      {/* Testimonials List */}
      <div className="space-y-4">
        {filteredTestimonials.map((testimonial) => (
          <div key={testimonial.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{testimonial.clientName}</h3>
                  <span className="text-sm text-gray-500">•</span>
                  <span className="text-sm text-gray-600">{testimonial.company}</span>
                  <span className="text-sm text-gray-500">•</span>
                  <span className="text-sm text-custom-brown">{testimonial.service}</span>
                </div>
                <div className="flex items-center space-x-2 mb-3">
                  <div className="flex">
                    {renderStars(testimonial.rating)}
                  </div>
                  <span className="text-sm text-gray-500">({testimonial.rating}/5)</span>
                </div>
                <p className="text-gray-700 mb-3 italic">"{testimonial.testimonial}"</p>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span>Date: {testimonial.date}</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${testimonial.status === 'published'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                    }`}>
                    {testimonial.status}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => setEditingTestimonial(testimonial)}
                className="bg-custom-brown/10 text-custom-brown px-3 py-2 rounded text-sm hover:bg-custom-brown/20 hover:text-custom-brown transition-colors duration-200"
              >
                Edit
              </button>
              <button
                onClick={() => toggleTestimonialStatus(testimonial.id)}
                className={`px-3 py-2 rounded text-sm transition-colors duration-200 ${testimonial.status === 'published'
                  ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
              >
                {testimonial.status === 'published' ? 'Unpublish' : 'Publish'}
              </button>
              <button
                onClick={() => handleDeleteTestimonial(testimonial.id)}
                className="bg-red-50 text-red-600 px-3 py-2 rounded text-sm hover:bg-red-100 hover:text-red-700 transition-colors duration-200"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Testimonial Modal */}
      {(showAddModal || editingTestimonial) && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddModal(false);
              setEditingTestimonial(null);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col max-h-[90vh] border border-gray-200">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
              </h3>
            </div>

            <form onSubmit={editingTestimonial ? handleEditTestimonial : handleAddTestimonial} className="flex flex-col min-h-0 flex-1">
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Client Name
                  </label>
                  <input
                    name="clientName"
                    type="text"
                    defaultValue={editingTestimonial?.clientName || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Company
                  </label>
                  <input
                    name="company"
                    type="text"
                    defaultValue={editingTestimonial?.company || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Service Used
                  </label>
                  <input
                    name="service"
                    type="text"
                    defaultValue={editingTestimonial?.service || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Rating
                  </label>
                  <select
                    name="rating"
                    defaultValue={editingTestimonial?.rating || 5}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                  >
                    {[1, 2, 3, 4, 5].map(rating => (
                      <option key={rating} value={rating}>{rating} Star{rating !== 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Testimonial
                  </label>
                  <textarea
                    name="testimonial"
                    rows="4"
                    defaultValue={editingTestimonial?.testimonial || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                    placeholder="Enter client testimonial..."
                  />
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-3 flex-shrink-0 rounded-b-lg">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingTestimonial(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-custom-brown text-white px-4 py-2 rounded-md hover:bg-custom-brown/90 transition-colors flex items-center justify-center font-medium shadow-sm"
                >
                  {editingTestimonial ? 'Update' : 'Add'} Testimonial
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsManager; 